// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useRef } from 'react';
import {
  Segment,
  Header,
  Icon,
  Button,
  Image,
  Message,
  Label
} from 'semantic-ui-react';
import { resizeImage } from '@loa/ui-components';
import { advisorApi } from '../api/advisorApi';

interface AdvisorPhotoUploadProps {
  advisorId: string;
  currentPhotoUrl?: string;
  advisorName: string;
  onPhotoUpdate?: (photoUrl: string | null) => void;
}

function AdvisorPhotoUpload({
  advisorId,
  currentPhotoUrl,
  advisorName,
  onPhotoUpdate
}: Readonly<AdvisorPhotoUploadProps>) {
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(currentPhotoUrl);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setSuccess(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG, PNG)');
      return;
    }

    try {
      // Resize image client-side before upload
      const resizedBlob = await resizeImage(file, 600);
      const resizedFile = new File([resizedBlob], file.name.replace(/\.\w+$/, '.jpg'), {
        type: 'image/jpeg',
      });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(resizedBlob);

      // Upload immediately
      handleUpload(resizedFile);
    } catch {
      setError('Failed to process image. Please try again.');
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await advisorApi.uploadPhoto(advisorId, file);
      setPhotoUrl(response.photoUrl);
      setPreviewUrl(null);
      setSuccess('Photo uploaded successfully!');

      // Notify parent component
      if (onPhotoUpdate) {
        onPhotoUpdate(response.photoUrl);
      }

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!globalThis.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setDeleting(true);
    setError(null);
    setSuccess(null);

    try {
      await advisorApi.deletePhoto(advisorId);
      setPhotoUrl(undefined);
      setSuccess('Photo deleted successfully');

      // Notify parent component
      if (onPhotoUpdate) {
        onPhotoUpdate(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo');
    } finally {
      setDeleting(false);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Segment>
      <Header as='h4'>
        <Icon name='user circle' />
        <Header.Content>
          Advisor Photo
          <Header.Subheader>Upload a professional photo for {advisorName}</Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>{success}</p>
        </Message>
      )}

      <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>
        {previewUrl && (
          <Segment placeholder>
            <Header icon>
              <Icon name='image' />
              Preview
            </Header>
            <Image
              src={previewUrl}
              size='small'
              centered
              circular
              style={{ maxWidth: '200px', maxHeight: '200px' }}
            />
          </Segment>
        )}
        {!previewUrl && photoUrl && (
          <div>
            <Image
              src={photoUrl}
              size='small'
              centered
              circular
              style={{ maxWidth: '200px', maxHeight: '200px' }}
            />
            <Button
              color='red'
              size='small'
              onClick={handleDelete}
              loading={deleting}
              disabled={uploading}
              style={{ marginTop: '10px' }}
            >
              <Icon name='trash' />
              Delete Photo
            </Button>
          </div>
        )}
        {!previewUrl && !photoUrl && (
          <Segment placeholder>
            <Header icon>
              <Icon name='user circle outline' />
              No Photo Uploaded
            </Header>
            <p>Upload a professional photo for this advisor</p>
          </Segment>
        )}
      </div>

      <input
        ref={fileInputRef}
        type='file'
        accept='image/jpeg,image/jpg,image/png'
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <Button
        primary
        fluid
        onClick={handleButtonClick}
        loading={uploading}
        disabled={deleting}
      >
        <Icon name='upload' />
        {photoUrl ? 'Replace Photo' : 'Upload Photo'}
      </Button>

      <Label style={{ marginTop: '10px', display: 'block' }}>
        <Icon name='info circle' />
        Accepted formats: JPEG, PNG (images are automatically resized)
      </Label>
    </Segment>
  );
}

export default AdvisorPhotoUpload;
